/**
 * The transport seam.
 *
 * The store consumes a stream of validated `ServerMessage`s and knows nothing
 * about where they came from. `SocketTransport` is the live gateway;
 * `LocalDriver` is the offline driver. Invariant 1 is what makes that possible:
 * because no mechanic lets an opponent react to your action, a recorded stream
 * and a live stream are the same stream, and the store cannot tell them apart.
 */

import type { ClientMessage, ServerMessage } from '@geogame/shared';

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'failed';

export interface TransportStats {
  readonly rttMs: number;
  readonly offsetMs: number;
  readonly synced: boolean;
}

export interface GameTransport {
  connect(): void;
  close(): void;
  send(msg: ClientMessage): void;
  /** Returns an unsubscribe function. */
  onMessage(fn: (msg: ServerMessage) => void): () => void;
  onStatus(fn: (status: ConnectionStatus) => void): () => void;
  /** INVARIANT 2 — the only clock the UI is allowed to read. */
  serverNow(): number;
  stats(): TransportStats;
  /**
   * Fetch one round's sealed artwork bundle by its opaque token.
   *
   * Part of the transport rather than a free function because "where the bytes
   * live" is the one thing the two transports genuinely disagree about: the
   * socket transport asks the gateway that armed the round, and the local
   * driver hands back bytes it sealed itself a moment ago. Everything above
   * this line — the preload, the decrypt, the object URLs — is identical, which
   * is what makes offline practice exercise the real reveal path.
   *
   * Rejects rather than resolving empty, so a failed preload is visible.
   */
  fetchAsset(token: string): Promise<Uint8Array>;
}

/** Tiny typed emitter; both transports share it. */
export class Emitter<T> {
  private listeners = new Set<(value: T) => void>();

  on(fn: (value: T) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  emit(value: T): void {
    for (const fn of [...this.listeners]) fn(value);
  }

  clear(): void {
    this.listeners.clear();
  }
}
